/** Round to 2 decimal places (GST amounts in rupees). */
export function roundMoney2(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return Math.round(value * 100) / 100;
}

/** Unit rate is stored/displayed to 2 decimal places. */
export function roundRate2(rate: number): number {
  return roundMoney2(rate);
}

/** Line taxable amount: round rate first, then qty × rate, then round to 2 dp. */
export function lineAmountFromQtyRate(qty: number, rate: number): number {
  return roundMoney2((qty ?? 0) * roundRate2(rate ?? 0));
}

export function roundLineTaxAmounts(tax: {
  cgst: number;
  sgst: number;
  igst: number;
}): { cgst: number; sgst: number; igst: number } {
  return {
    cgst: roundMoney2(tax.cgst),
    sgst: roundMoney2(tax.sgst),
    igst: roundMoney2(tax.igst),
  };
}

export function sumLineTaxAmounts(tax: {
  cgst: number;
  sgst: number;
  igst: number;
}): number {
  return roundMoney2(tax.cgst + tax.sgst + tax.igst);
}

/** Sum of rounded line taxes — authoritative total for new documents (option 1A). */
export function grandTotalTaxFromLines(
  lines: Array<{ cgst?: number; sgst?: number; igst?: number }>
): number {
  return roundMoney2(
    lines.reduce((s, l) => s + (l.cgst ?? 0) + (l.sgst ?? 0) + (l.igst ?? 0), 0)
  );
}

/**
 * When stored header tax differs from line sum by ≤1 paisa, treat as legacy rounding
 * and keep displaying/saving the header value for existing documents.
 */
export function isLegacyTaxHeaderMismatch(lineTaxSum: number, headerTax: number): boolean {
  if (headerTax <= 0) return false;
  const diff = Math.abs(lineTaxSum - headerTax);
  return diff > 0.001 && diff <= 0.01 + 1e-9;
}

/** Footer/display total tax: line sum for new docs; header tax for legacy mismatches. */
export function resolveDisplayTotalTax(lineTaxSum: number, headerTax: number): number {
  if (headerTax > 0 && isLegacyTaxHeaderMismatch(lineTaxSum, headerTax)) {
    return headerTax;
  }
  return headerTax > 0 ? headerTax : lineTaxSum;
}
